import { Injectable, inject } from '@angular/core';
import { from, Observable } from 'rxjs';
import { getPublicUrl, PUBLIC_MEDIA_BUCKET, supabase } from '../../core/supabase/supabase.client';
import { ImageAssetUploadService } from '../utils/image-asset-upload.service';

export interface BottomDynamicTab {
  id?: number;
  collection_id: number;
  label: string;
  icon_file_id?: number | null;
  background_color?: string;
  text_color?: string;
  priority: number;
  is_active: boolean;
  start_at?: string;
  end_at?: string;
  created_at?: string;
  // joined fields
  icon_url?: string;
  collection_title?: string;
  collection_icon_url?: string;
}

@Injectable({ providedIn: 'root' })
export class DynamicTabsService {
  private readonly imageAssets = inject(ImageAssetUploadService);
  private readonly bucket = PUBLIC_MEDIA_BUCKET;

  getTabs(): Observable<BottomDynamicTab[]> {
    return from(
      supabase
        .from('bottom_dynamic_tabs')
        .select(`
          *,
          collections (
            title,
            icon_file:files!collections_icon_file_id_fkey(public_url, bucket, path)
          ),
          files ( public_url, bucket, path )
        `)
        .order('priority', { ascending: false })
        .then((res: any) => {
          if (res.error) throw res.error;
          // map joined data for easier consumption
          return (res.data as any[]).map(t => {
            let collectionIconUrl = null;
            if (t.collections?.icon_file) {
              const f = Array.isArray(t.collections.icon_file) ? t.collections.icon_file[0] : t.collections.icon_file;
              collectionIconUrl = f?.public_url || (f?.bucket && f?.path ? getPublicUrl(f.bucket, f.path) : null);
            }
            
            let tabIconUrl = null;
            if (t.files) {
              const tabF = Array.isArray(t.files) ? t.files[0] : t.files;
              tabIconUrl = tabF?.public_url || (tabF?.bucket && tabF?.path ? getPublicUrl(tabF.bucket, tabF.path) : null);
            }

            return {
              ...t,
              collection_title: t.collections?.title,
              icon_url: tabIconUrl,
              collection_icon_url: collectionIconUrl
            };
          }) as BottomDynamicTab[];
        })
    );
  }

  createTab(tab: Partial<BottomDynamicTab>): Observable<BottomDynamicTab> {
    return from(
      supabase.from('bottom_dynamic_tabs').insert([tab]).select().single().then((res: any) => {
        if (res.error) throw res.error;
        return res.data as BottomDynamicTab;
      })
    );
  }

  updateTab(id: number, tab: Partial<BottomDynamicTab>): Observable<BottomDynamicTab> {
    return from(
      supabase.from('bottom_dynamic_tabs').update(tab).eq('id', id).select().single().then((res: any) => {
        if (res.error) throw res.error;
        return res.data as BottomDynamicTab;
      })
    );
  }

  deleteTab(id: number): Observable<void> {
    return from(
      supabase.from('bottom_dynamic_tabs').delete().eq('id', id).then((res: any) => {
        if (res.error) throw res.error;
      })
    );
  }

  uploadTabMedia(file: File): Observable<number> {
    return from(
      (async () => {
        const asset = await this.imageAssets.upload({
          file,
          bucket: this.bucket,
          basePath: 'dynamic_tabs',
          kind: 'home_tab_icon',
          entityType: 'bottom_dynamic_tab',
          purpose: 'icon',
          isPrimary: true,
        });
        return asset.fileId;
      })()
    );
  }
}
